/**
 * COYG's navigation is a single ordered stack of "layers" on top of the
 * active tab. A layer is a match detail, a player detail, a codex entry, or a
 * match-lore entry. Pushing opens; popping is the universal "back". Keeping it
 * a pure reducer lets the system Back button, the on-screen back control, and
 * the edge-swipe all funnel through one `pop` — so you can never get buried.
 */
export type NavLayer =
  | { kind: 'match'; id: string }
  | { kind: 'player'; id: string }
  | { kind: 'entry'; id: string }
  | { kind: 'lore'; id: string }
  | { kind: 'year'; id: string };

export interface NavState {
  layers: NavLayer[];
}

export type NavAction =
  | { type: 'push'; layer: NavLayer }
  | { type: 'replace-top'; layer: NavLayer }
  | { type: 'pop' }
  | { type: 'reset'; layers: NavLayer[] };

const sameLayer = (a: NavLayer, b: NavLayer) => a.kind === b.kind && a.id === b.id;

export function navReduce(state: NavState, action: NavAction): NavState {
  switch (action.type) {
    case 'push': {
      const top = state.layers.at(-1);
      if (top && sameLayer(top, action.layer)) return state;
      return { layers: [...state.layers, action.layer] };
    }
    case 'replace-top': {
      const top = state.layers.at(-1);
      if (!top) return { layers: [action.layer] };
      if (sameLayer(top, action.layer)) return state;
      return { layers: [...state.layers.slice(0, -1), action.layer] };
    }
    case 'pop':
      return state.layers.length === 0 ? state : { layers: state.layers.slice(0, -1) };
    case 'reset':
      return { layers: action.layers };
  }
}

export const topLayer = (s: NavState): NavLayer | null => s.layers.at(-1) ?? null;
